// R7 — Bookmarks. R7a (anonymous, always on) is a complete localStorage store:
// saved bookmarks plus one "current position" record per chapter, shaped as the
// Convex `Bookmark` minus its server-derived owner fields (LocalBookmark). All
// storage goes through the single `prefs` gateway so keys stay in one place.
//
// R7b (signed-in cross-device sync) is BLOCKED on PD-1 (auth issuer scheme) for
// a live test, so the cloud path lands here as a typed seam only: `mergeLocalToCloud`
// takes a transport that phase 3's `bookmarks.mergeLocal` mutation implements,
// with the Convex identity (`convex/model/session.ts`) supplying the owner
// server-side. Union-by-position + dedupe, never clobber (SPEC "Auth"). Nothing
// here blocks on live auth.

import { getPref, setPref } from "@/lib/prefs";
import type { RetellingLocation } from "@bindery/convex-retells/model/location";
import type { LocalBookmark } from "@bindery/shared/corpus/reader";

/** All stored records (bookmarks + position records), newest-first is not assumed. */
export function loadAll(): LocalBookmark[] {
  return getPref("bookmarks", []);
}

function saveAll(records: LocalBookmark[]): void {
  setPref("bookmarks", records);
}

function samePosition(a: RetellingLocation, b: RetellingLocation): boolean {
  return a.blockId === b.blockId && a.offset === b.offset;
}

/** Saved bookmarks (kind `"bookmark"`), optionally scoped to one chapter. */
export function listBookmarks(
  bookSlug?: string,
  chapterNumber?: number,
): LocalBookmark[] {
  return loadAll().filter(
    (r) =>
      r.kind === "bookmark" &&
      (bookSlug === undefined || r.bookSlug === bookSlug) &&
      (chapterNumber === undefined || r.chapterNumber === chapterNumber),
  );
}

/** The current-position record for a chapter, if one was ever saved. */
export function getPosition(
  bookSlug: string,
  chapterNumber: number,
): LocalBookmark | undefined {
  return loadAll().find(
    (r) =>
      r.kind === "position" &&
      r.bookSlug === bookSlug &&
      r.chapterNumber === chapterNumber,
  );
}

/**
 * The most recently saved position record for a book, if any — drives the
 * library's "Continue reading" entry. `savePosition` re-appends the record it
 * upserts, so the LAST matching record in storage order is the most recent
 * write; no timestamp is stored (or needed) for this.
 */
export function latestPosition(bookSlug: string): LocalBookmark | undefined {
  const matches = loadAll().filter(
    (r) => r.kind === "position" && r.bookSlug === bookSlug,
  );
  // Index, not `.at(-1)` (Chrome 92 / Safari 15.4, past the BROWSER_FLOOR).
  return matches[matches.length - 1];
}

/**
 * Upsert the current reading position for a chapter — exactly one `"position"`
 * record per (book, chapter). Called on scroll (throttled by the caller) so a
 * reopen resumes where the reader left off.
 */
export function savePosition(
  bookSlug: string,
  chapterNumber: number,
  location: RetellingLocation,
): void {
  const records = loadAll().filter(
    (r) =>
      !(
        r.kind === "position" &&
        r.bookSlug === bookSlug &&
        r.chapterNumber === chapterNumber
      ),
  );
  records.push({ kind: "position", bookSlug, chapterNumber, location });
  saveAll(records);
}

/**
 * Add a saved bookmark, deduped by exact position (SPEC "Auth": union by
 * position, never duplicate). Returns the full updated bookmark list for the
 * chapter so the caller can reflect it without a re-read.
 */
export function addBookmark(
  bookSlug: string,
  chapterNumber: number,
  location: RetellingLocation,
  label?: string,
): LocalBookmark[] {
  const records = loadAll();
  const exists = records.some(
    (r) =>
      r.kind === "bookmark" &&
      r.bookSlug === bookSlug &&
      r.chapterNumber === chapterNumber &&
      samePosition(r.location, location),
  );
  if (!exists) {
    const bookmark: LocalBookmark =
      label === undefined
        ? { kind: "bookmark", bookSlug, chapterNumber, location }
        : { kind: "bookmark", bookSlug, chapterNumber, location, label };
    records.push(bookmark);
    saveAll(records);
  }
  return listBookmarks(bookSlug, chapterNumber);
}

/** Remove a saved bookmark by exact position. Returns the chapter's remainder. */
export function removeBookmark(
  bookSlug: string,
  chapterNumber: number,
  location: RetellingLocation,
): LocalBookmark[] {
  const records = loadAll().filter(
    (r) =>
      !(
        r.kind === "bookmark" &&
        r.bookSlug === bookSlug &&
        r.chapterNumber === chapterNumber &&
        samePosition(r.location, location)
      ),
  );
  saveAll(records);
  return listBookmarks(bookSlug, chapterNumber);
}

/** Whether an exact position is already bookmarked. */
export function isBookmarked(
  bookSlug: string,
  chapterNumber: number,
  location: RetellingLocation,
): boolean {
  return listBookmarks(bookSlug, chapterNumber).some((r) =>
    samePosition(r.location, location),
  );
}

// --------------------------------------------------------- R7b cloud seam ---

/**
 * Transport for cross-device bookmark sync. Phase 3's `bookmarks.mergeLocal`
 * mutation implements this: it takes the anonymous localStorage records and
 * unions them into the signed-in user's Convex rows (dedup by position, never
 * clobbering either side), deriving the owner from the WorkOS session
 * identity server-side (`convex/model/session.ts`) — the local records
 * deliberately omit it.
 *
 * SEAM (R7b, blocked on PD-1): wire a real implementation once the auth issuer
 * scheme lands. Until then the reader runs the localStorage path only, and this
 * is called with a live transport at sign-in without any change here.
 */
export interface BookmarkCloudSync {
  mergeLocal(records: LocalBookmark[]): Promise<void>;
}

/**
 * On sign-in, push the local records into the cloud via the transport, then the
 * caller subscribes to `bookmarks.list` for ongoing cross-device sync. A no-op
 * when there is nothing local to merge.
 */
export async function mergeLocalToCloud(
  sync: BookmarkCloudSync,
): Promise<void> {
  const records = loadAll();
  if (records.length === 0) return;
  await sync.mergeLocal(records);
}
